import { LabeledValue } from "components/designV2/labeledValue/labeledValue";
import { Modal } from "components/designV2/modal/modal";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { CompanyCustomer, CompanyCustomerProduct } from "model/Company";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAlertStore } from "stores/alertStore/alertStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { AddCustomerServiceForm } from "./addCustomerServicePanel.component";
import {
  CustomerSectionAction,
  CustomerModalContent,
  CustomerProductEmpty,
  CustomerProductRow,
  CustomerProductsList,
  CustomerSection,
  CustomerSectionHeader,
  CustomerSectionTitle,
  CustomerSummaryGrid,
} from "./customerPanel.styled";
import { useCustomerProducts } from "./useCustomerProducts.hook";

type Props = {
  customer: CompanyCustomer | null;
  setShowCustomerModal: React.Dispatch<React.SetStateAction<boolean>>;
  showCustomerModal: boolean;
  setCustomers(customers: readonly CompanyCustomer[]): void;
};

const formatDateValue = (value?: Date): string => {
  if (!value) return "-";

  const dateString =
    typeof value === "string" ? value : new Date(value).toISOString();
  const formattedValue = dateString.split("T")[0];

  if (!formattedValue || formattedValue.startsWith("0001")) {
    return "-";
  }

  return formattedValue;
};

export const CustomerModal: React.FC<Props> = ({
  setShowCustomerModal,
  showCustomerModal,
  customer,
  setCustomers,
}) => {
  const [showAddCustomerProduct, setShowAddCustomerProduct] = useState(false);
  const [companyState] = useCompanyStore();
  const { t } = useTranslation();
  const [, , alertDispatch] = useAlertStore();
  const { isSavingProduct, saveCustomerProduct } = useCustomerProducts();

  const products = useMemo(
    (): readonly CompanyCustomerProduct[] =>
      customer?.agreements?.[0]?.products ?? [],
    [customer],
  );

  const onSaveProduct = async (
    product: CompanyCustomerProduct,
  ): Promise<boolean> => {
    const companyId = companyState.company?.id;
    const updatedCustomers = await saveCustomerProduct({ companyId, product });

    if (!updatedCustomers) {
      alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          icon: "alert",
          title: t("Alerts.OhNo"),
          message: t("General.ErrorOccurred"),
        },
      });
      return false;
    }

    setCustomers([...updatedCustomers]);
    return true;
  };

  const handleClose = (): void => {
    setShowAddCustomerProduct(false);
    setShowCustomerModal(false);
  };

  return (
    <>
      <Modal
        isOpen={showCustomerModal}
        onClose={handleClose}
        label={customer?.name ?? t("CompanyCustomers.Customer")}
        size="medium"
      >
        <CustomerModalContent>
          {customer && (
            <CustomerSection>
              <CustomerSectionHeader>
                <CustomerSectionTitle>
                  {t("CompanyCustomers.CustomerInformation")}
                </CustomerSectionTitle>
              </CustomerSectionHeader>
              <CustomerSummaryGrid>
                <LabeledValue
                  label={t("CompanyCustomers.Name")}
                  value={customer.name}
                  variant="small"
                />
                <LabeledValue
                  label={t("CompanyCustomers.OrganizationNumber")}
                  value={customer.organizationNumber}
                  variant="small"
                />
                <LabeledValue
                  label={t("CompanyCustomers.Contact")}
                  value={customer.contact}
                  variant="small"
                />
              </CustomerSummaryGrid>
            </CustomerSection>
          )}

          <CustomerSection>
            <CustomerSectionHeader>
              <CustomerSectionTitle>
                {t("CompanyCustomers.Products")}
              </CustomerSectionTitle>
            </CustomerSectionHeader>

            {products.length === 0 ? (
              <CustomerProductEmpty>{t("General.None")}</CustomerProductEmpty>
            ) : (
              <CustomerProductsList>
                {products.map((product) => (
                  <CustomerProductRow key={product.id ?? product.title}>
                    <LabeledValue
                      label={t("CompanyCustomers.Title")}
                      value={product.title}
                      variant="small"
                      truncate
                    />
                    <LabeledValue
                      label={t("CompanyCustomers.Price")}
                      value={String(product.price)}
                      variant="small"
                    />
                    <LabeledValue
                      label={t("CompanyCustomers.StartDate")}
                      value={formatDateValue(product.startDate)}
                      variant="small"
                    />
                    <LabeledValue
                      label={t("CompanyCustomers.EndDate")}
                      value={formatDateValue(product.endDate)}
                      variant="small"
                    />
                    <LabeledValue
                      label={t("CompanyCustomers.Comment")}
                      value={product.comment || "-"}
                      variant="small"
                      truncate
                    />
                  </CustomerProductRow>
                ))}
              </CustomerProductsList>
            )}

            <CustomerSectionAction>
              <SecondaryButton
                label={t("CompanyCustomers.AddMoreProducts")}
                iconName="v2/plus"
                size="small"
                onClick={() => {
                  if (customer?.id) {
                    setShowAddCustomerProduct(true);
                  }
                }}
                type="button"
              />
            </CustomerSectionAction>
          </CustomerSection>
        </CustomerModalContent>
      </Modal>

      <AddCustomerServiceForm
        customer={customer}
        setShowAddCustomerProductForm={setShowAddCustomerProduct}
        showAddCustomerServiceForm={showAddCustomerProduct}
        isSaving={isSavingProduct}
        saveProduct={onSaveProduct}
      />
    </>
  );
};
